import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  FaDownload,
  FaMagic,
  FaRedo,
  FaWhatsapp,
  FaImage,
  FaUndo,
  FaGem,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCalendarAlt,
  FaBuilding,
  FaEye,
  FaEdit,
  FaCheckCircle,
} from "react-icons/fa";
import { IoSparklesSharp } from "react-icons/io5";
import { useSelector } from "react-redux";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";

const inputCls =
  "w-full h-9 px-3 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 border-slate-200 focus:ring-violet-300 focus:border-violet-300 transition placeholder:text-slate-400";

const textareaCls =
  "w-full h-[76px] px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 border-slate-200 focus:ring-violet-300 focus:border-violet-300 transition placeholder:text-slate-400 resize-none";

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number") return String(value);
  return fallback;
};

const buildAddress = (obj) => {
  if (!obj) return "";

  return [
    safeText(obj.addressLine1),
    safeText(obj.city),
    safeText(obj.state),
    safeText(obj.pincode),
  ]
    .filter(Boolean)
    .join(", ");
};

const getCompanyFromBranch = (branch) => {
  if (!branch?.company) return null;
  if (typeof branch.company === "object") return branch.company;
  return null;
};

const AIMarketingPostGenerator = () => {
  const authUser = useSelector((s) => s.auth?.user);

  const {
    data: branchesResp,
    isLoading: isBranchesLoading,
    error: branchesError,
  } = useGetAllBranchesQuery({ page: 1, limit: 100, q: "", active: true });

  const branches = useMemo(() => {
    return branchesResp?.data || branchesResp?.items || branchesResp?.rows || [];
  }, [branchesResp]);

  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [creativeNote, setCreativeNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [mobilePanel, setMobilePanel] = useState("setup");

  /*
    Later backend calendar API response yaha set karna:

    const todayEvent = {
      isSpecialDay: true,
      eventName: "Dhanteras",
      eventType: "festival",
      promptHint: "Create premium Dhanteras jewellery poster..."
    };

    Abhi null hai, so normal daily post suggest hoga.
  */
  const todayEvent = null;

  useEffect(() => {
    if (!branches?.length || selectedBranchId) return;

    const staffBranchId =
      authUser?.employeeDetails?.branch?._id ||
      authUser?.employeeDetails?.branch ||
      "";

    const matched = branches.find(
      (branch) => String(branch?._id) === String(staffBranchId),
    );

    setSelectedBranchId(matched?._id || branches?.[0]?._id || "");
  }, [branches, selectedBranchId, authUser]);

  const selectedBranch = useMemo(() => {
    return branches.find(
      (branch) => String(branch?._id) === String(selectedBranchId),
    );
  }, [branches, selectedBranchId]);

  const company = useMemo(() => {
    return getCompanyFromBranch(selectedBranch);
  }, [selectedBranch]);

  const brand = useMemo(() => {
    const shopName =
      safeText(company?.companyName) ||
      safeText(company?.shortName) ||
      safeText(authUser?.tenantName) ||
      safeText(authUser?.companyName) ||
      safeText(authUser?.organisationName) ||
      safeText(authUser?.userName) ||
      "Your Jewellery Shop";

    const branchName =
      safeText(selectedBranch?.branchName) ||
      safeText(authUser?.employeeDetails?.branch?.branchName) ||
      "Main Branch";

    const contact =
      safeText(selectedBranch?.contact) ||
      safeText(company?.contact) ||
      safeText(authUser?.mobile) ||
      safeText(authUser?.phone) ||
      "9876543210";

    const branchAddress = buildAddress(selectedBranch);
    const companyAddress = buildAddress(company);

    const address =
      branchAddress || companyAddress || "Shop address will appear here";

    return {
      shopName,
      branchName,
      contact,
      address,
      tagline: "Trusted Gold • Silver • Diamond Jewellery",
    };
  }, [company, selectedBranch, authUser]);

  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const titleText = useMemo(() => {
    if (todayEvent?.isSpecialDay && todayEvent?.eventName) {
      return `${todayEvent.eventName} की हार्दिक शुभकामनाएं`;
    }

    return "हर दिन चमके आपकी खूबसूरती";
  }, [todayEvent]);

  const subTitleText = useMemo(() => {
    if (creativeNote?.trim()) return creativeNote.trim();

    if (todayEvent?.isSpecialDay) {
      return "Celebrate this special occasion with elegance, purity and trust.";
    }

    return "Fresh designs, trusted purity and elegant jewellery collections.";
  }, [creativeNote, todayEvent]);

  const calendarTitle = todayEvent?.isSpecialDay
    ? `${todayEvent.eventName} Detected`
    : "No Special Event Today";

  const calendarDescription = todayEvent?.isSpecialDay
    ? "Festival based jewellery marketing post will be generated automatically."
    : "Normal daily jewellery branding post will be generated automatically.";

  const handleGenerate = async () => {
    if (!selectedBranchId) {
      toast.error("Please select branch.");
      return;
    }

    try {
      setLoading(true);

      /*
        Later backend API payload:

        const payload = {
          branchId: selectedBranchId,
          companyId: company?._id,
          postType: todayEvent?.isSpecialDay ? "festival" : "normal",
          todayEvent,
          creativeNote,
          brand: {
            shopName: brand.shopName,
            branchName: brand.branchName,
            contact: brand.contact,
            address: brand.address,
            tagline: brand.tagline,
          },
        };

        const res = await generateAIMarketingPost(payload).unwrap();
      */

      await new Promise((resolve) => setTimeout(resolve, 650));

      setGenerated(true);
      setMobilePanel("preview");
      toast.success("Marketing post generated.");
    } catch (err) {
      toast.error("Failed to generate post.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCreativeNote("");
    setGenerated(false);
  };

  const handleDownload = () => {
    toast.success("Download API integrate karna baki hai.");
  };

  const handleOpenWhatsApp = () => {
    window.open("https://web.whatsapp.com", "_blank");
  };

  return (
    <div className="h-[calc(100dvh-88px)] w-full overflow-hidden">
      <div className="h-full min-h-0 flex flex-col gap-2">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
          className="relative shrink-0 overflow-hidden bg-white rounded-[20px] border border-violet-100 shadow-sm px-3 sm:px-4 py-2.5"
        >
          <div className="absolute right-0 top-0 h-20 w-20 rounded-full bg-violet-100/60 blur-2xl" />
          <div className="absolute left-1/2 bottom-0 h-16 w-16 rounded-full bg-amber-100/70 blur-2xl" />

          <div className="relative flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-[#9481ce] to-[#f2d6b3] text-white grid place-items-center shadow-sm shrink-0">
                <IoSparklesSharp size={18} />
              </div>

              <div className="min-w-0">
                <h1 className="text-sm sm:text-base font-semibold text-slate-800 truncate">
                  AI Marketing Post Generator
                </h1>
                <p className="hidden sm:block text-[11px] text-slate-500 truncate">
                  Daily branded jewellery posts for WhatsApp groups.
                </p>
              </div>
            </div>

            <div className="hidden md:flex flex-wrap items-center gap-2">
              <Badge icon={<FaCalendarAlt />} text={todayLabel} />
              <Badge icon={<FaImage />} text="365 Creatives" />
              <Badge icon={<FaGem />} text="No Rate In Poster" amber />
            </div>

            <div className="md:hidden inline-flex items-center rounded-full bg-violet-50 border border-violet-100 p-1">
              <button
                type="button"
                onClick={() => setMobilePanel("setup")}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold ${
                  mobilePanel === "setup"
                    ? "bg-[#9481ce] text-white"
                    : "text-[#7d70b2]"
                }`}
              >
                <FaEdit size={10} />
                Setup
              </button>

              <button
                type="button"
                onClick={() => setMobilePanel("preview")}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold ${
                  mobilePanel === "preview"
                    ? "bg-[#9481ce] text-white"
                    : "text-[#7d70b2]"
                }`}
              >
                <FaEye size={10} />
                Preview
              </button>
            </div>
          </div>
        </motion.div>

        {/* Main Area */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] xl:grid-cols-[380px_minmax(0,1fr)] gap-2">
          {/* Left Setup Panel */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22 }}
            className={`min-h-0 bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden ${
              mobilePanel === "setup" ? "flex" : "hidden"
            } lg:flex flex-col`}
          >
            <SectionHeader
              title="Post Setup"
              subtitle="Only branch and optional note required."
            />

            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
              {/* Calendar Insight */}
              <div
                className={`rounded-2xl border p-3 ${
                  todayEvent?.isSpecialDay
                    ? "border-amber-100 bg-amber-50"
                    : "border-violet-100 bg-gradient-to-br from-violet-50 to-white"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={`h-8 w-8 rounded-xl grid place-items-center shadow-sm shrink-0 ${
                      todayEvent?.isSpecialDay
                        ? "bg-white text-amber-700"
                        : "bg-white text-[#7d70b2]"
                    }`}
                  >
                    <FaCalendarAlt size={13} />
                  </div>

                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-800">
                      Today’s Calendar Insight
                    </div>

                    <div className="mt-1 flex items-center gap-1.5">
                      <FaCheckCircle
                        size={11}
                        className={
                          todayEvent?.isSpecialDay
                            ? "text-amber-700"
                            : "text-[#7d70b2]"
                        }
                      />
                      <span className="text-[11px] font-semibold text-slate-700">
                        {calendarTitle}
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                      {calendarDescription}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company & Branch Master */}
              <div className="rounded-2xl border border-slate-100 bg-slate-50/80 overflow-hidden">
                <div className="px-3 py-2 border-b bg-white flex items-center gap-2">
                  <FaBuilding className="text-[#7d70b2] text-xs" />
                  <span className="text-xs font-semibold text-slate-800">
                    Company & Branch Details
                  </span>
                </div>

                <div className="p-3 space-y-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Select Branch
                    </label>
                    <select
                      value={selectedBranchId}
                      onChange={(e) => {
                        setSelectedBranchId(e.target.value);
                        setGenerated(false);
                      }}
                      disabled={isBranchesLoading || !!branchesError}
                      className={inputCls}
                    >
                      <option value="">
                        {isBranchesLoading
                          ? "Loading branches..."
                          : branchesError
                          ? "Failed to load branches"
                          : "Select branch"}
                      </option>

                      {branches.map((branch) => (
                        <option key={branch._id} value={branch._id}>
                          {branch.branchName || branch.name || "Branch"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <ReadOnlyInfo label="Shop" value={brand.shopName} />
                    <ReadOnlyInfo label="Branch" value={brand.branchName} />
                    <ReadOnlyInfo label="Mobile" value={brand.contact} />
                    <ReadOnlyInfo label="Address" value={brand.address} />
                  </div>
                </div>
              </div>

              {/* Optional Note */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Optional Creative Note
                </label>
                <textarea
                  value={creativeNote}
                  onChange={(e) => {
                    setCreativeNote(e.target.value);
                    setGenerated(false);
                  }}
                  placeholder="e.g. Bridal collection, trust, purity, festive greeting..."
                  className={textareaCls}
                />
              </div>

              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-3">
                <div className="text-xs font-semibold text-amber-800 mb-1">
                  Poster Rule
                </div>
                <p className="text-[11px] text-amber-700 leading-relaxed">
                  Poster me rate nahi jayega. Sirf shop branding, branch,
                  contact, address aur occasion based creative rahega.
                </p>
              </div>
            </div>

            <div className="border-t bg-white px-3 py-2.5 shrink-0">
              <div className="grid grid-cols-2 gap-2">
                <PrimaryButton
                  icon={<FaMagic />}
                  label={loading ? "Generating..." : "Generate"}
                  onClick={handleGenerate}
                  disabled={loading || !selectedBranchId}
                />

                <SecondaryButton
                  icon={<FaUndo />}
                  label="Reset"
                  onClick={handleReset}
                />
              </div>
            </div>
          </motion.div>

          {/* Right Preview Panel */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, delay: 0.03 }}
            className={`min-h-0 bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden ${
              mobilePanel === "preview" ? "flex" : "hidden"
            } lg:flex flex-col`}
          >
            <SectionHeader
              title="Preview"
              subtitle="Your final WhatsApp poster."
              right={
                <span
                  className={`shrink-0 text-[10px] px-2.5 py-1 rounded-full border font-semibold ${
                    generated
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                >
                  {generated ? "Ready" : "Draft"}
                </span>
              }
            />

            <div className="flex-1 min-h-0 flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50/70 p-2 sm:p-3 overflow-hidden">
              <PosterPreview
                brand={brand}
                generated={generated}
                titleText={titleText}
                subTitleText={subTitleText}
                todayEvent={todayEvent}
              />
            </div>

            <div className="shrink-0 border-t bg-white px-3 py-2.5">
              <div className="grid grid-cols-3 gap-2">
                <ActionButton
                  icon={<FaRedo />}
                  label="Regenerate"
                  onClick={handleGenerate}
                  disabled={loading || !selectedBranchId}
                  className="border border-violet-200 bg-violet-50 text-[#7d70b2] hover:bg-violet-100"
                />

                <ActionButton
                  icon={<FaDownload />}
                  label="Download"
                  onClick={handleDownload}
                  disabled={!generated}
                  className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                />

                <ActionButton
                  icon={<FaWhatsapp />}
                  label="WhatsApp"
                  onClick={handleOpenWhatsApp}
                  disabled={!generated}
                  className="bg-[#25D366] text-white hover:brightness-95 disabled:opacity-60"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const PosterPreview = ({ brand, generated, titleText, subTitleText, todayEvent }) => {
  return (
    <div className="relative h-full max-h-full max-w-full aspect-[4/5] rounded-[26px] sm:rounded-[30px] overflow-hidden shadow-2xl border border-white bg-gradient-to-br from-[#211039] via-[#7455b4] to-[#f2d6b3]">
      <div className="absolute inset-0 opacity-35 bg-[radial-gradient(circle_at_18%_12%,_#ffffff,_transparent_28%),radial-gradient(circle_at_82%_85%,_#facc15,_transparent_30%)]" />
      <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-amber-300/20 blur-2xl" />

      <div className="relative h-full p-4 sm:p-6 flex flex-col justify-between text-white">
        {/* Top Brand */}
        <div className="flex items-center justify-between gap-3">
          <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl bg-white/18 border border-white/30 grid place-items-center text-base sm:text-lg font-bold shadow-sm shrink-0">
            {brand.shopName?.charAt(0)?.toUpperCase() || "J"}
          </div>

          <div className="text-right min-w-0">
            <div className="text-[12px] sm:text-[13px] font-semibold leading-tight truncate max-w-[200px] sm:max-w-[220px]">
              {brand.shopName}
            </div>
            <div className="text-[10px] opacity-80 truncate max-w-[200px] sm:max-w-[220px]">
              {brand.branchName}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="text-center space-y-3 px-1">
          <div className="inline-flex px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">
            {todayEvent?.isSpecialDay ? "Festive Jewellery" : "Daily Jewellery"}
          </div>

          <h3 className="text-[21px] sm:text-[26px] font-bold leading-tight drop-shadow">
            {generated ? titleText : "Your Daily Shop Post"}
          </h3>

          <p className="text-[12px] sm:text-sm opacity-90 leading-relaxed max-h-[58px] overflow-hidden">
            {generated
              ? subTitleText
              : "Click generate to create a fresh branded jewellery marketing post."}
          </p>
        </div>

        {/* Footer */}
        <div className="rounded-3xl bg-white/15 border border-white/25 p-3 sm:p-3.5 backdrop-blur-sm space-y-2">
          <div className="text-[12px] sm:text-sm font-semibold truncate">
            {brand.tagline}
          </div>

          <div className="space-y-1 text-[10px] sm:text-[10.5px] opacity-90">
            <div className="flex items-center gap-2">
              <FaPhoneAlt className="shrink-0" />
              <span className="truncate">{brand.contact}</span>
            </div>

            <div className="flex items-center gap-2">
              <FaMapMarkerAlt className="shrink-0" />
              <span className="truncate">{brand.address}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, subtitle, right }) => {
  return (
    <div className="px-3 py-2.5 border-b bg-slate-50/70 flex items-center justify-between gap-3 shrink-0">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-slate-800 truncate">
          {title}
        </h2>
        <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>
      </div>
      {right}
    </div>
  );
};

const Badge = ({ icon, text, amber = false }) => {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
        amber
          ? "bg-amber-50 border-amber-100 text-amber-700"
          : "bg-violet-50 border-violet-100 text-[#7d70b2]"
      }`}
    >
      <span className="text-[10px]">{icon}</span>
      {text}
    </span>
  );
};

const ReadOnlyInfo = ({ label, value }) => {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2">
      <span className="text-[11px] text-slate-500 shrink-0">{label}</span>
      <span className="text-[11px] font-semibold text-slate-800 text-right max-h-[34px] overflow-hidden">
        {value || "—"}
      </span>
    </div>
  );
};

const PrimaryButton = ({ icon, label, onClick, disabled }) => {
  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.01, y: -1 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9481ce] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[10px]">
        {icon}
      </span>
      {label}
    </motion.button>
  );
};

const SecondaryButton = ({ icon, label, onClick }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.01, y: -1 }}
      whileTap={{ scale: 0.98 }}
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-100 transition"
    >
      <span className="text-[10px]">{icon}</span>
      {label}
    </motion.button>
  );
};

const ActionButton = ({ icon, label, onClick, disabled, className = "" }) => {
  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.01, y: -1 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full px-2.5 sm:px-3.5 py-2 text-[10px] sm:text-xs font-semibold shadow-sm transition disabled:cursor-not-allowed ${className}`}
    >
      <span className="text-[10px] sm:text-[11px]">{icon}</span>
      <span className="truncate">{label}</span>
    </motion.button>
  );
};

export default AIMarketingPostGenerator;